import { useId, useRef, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "./motion/select";

type Option = { value: string; label: string; disabled?: boolean };

/** Message editors retain native required-field validation while presenting the
 * installed beUI option list inside their owning dialog. */
export function MessageFormSelect({ label, value, onChange, options, placeholder, required = false, disabled = false, autoFocus = false }: {
  label: string; value: string; onChange: (value: string) => void; options: Option[];
  placeholder?: string; required?: boolean; disabled?: boolean; autoFocus?: boolean;
}) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  return <div ref={root} className="min-w-0 space-y-1" onKeyDown={event => {
    if (event.key !== "Escape" || !open) return;
    // Dismiss the list before the parent editor handles Escape or native cancel.
    event.preventDefault(); event.stopPropagation(); setOpen(false);
    root.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }}>
    <label htmlFor={id} className="block text-xs font-semibold text-navy">{label}</label>
    <Select id={id} value={value} open={open} onOpenChange={setOpen} disabled={disabled} onValueChange={next => { setInvalid(false); onChange(next); }}>
      <SelectTrigger aria-label={label} aria-invalid={invalid} aria-describedby={invalid ? `${id}-error` : undefined} className={`min-h-11 text-left text-base font-normal sm:text-sm ${invalid ? "border-brand-red" : ""}`}>
        <span className={value ? "" : "text-mut"}>{options.find(option => option.value === value)?.label || placeholder || "Select…"}</span>
      </SelectTrigger>
      <SelectContent className="z-50 max-h-60 w-full overflow-y-auto border-line bg-white">
        {placeholder && <SelectItem value="" textValue={placeholder}>{placeholder}</SelectItem>}
        {options.map(option => <SelectItem key={option.value} value={option.value} disabled={option.disabled} textValue={option.label}>{option.label}</SelectItem>)}
      </SelectContent>
    </Select>
    {/* The bridge blocks form submission; invalid focus returns to the visible trigger. */}
    <select aria-hidden="true" tabIndex={-1} className="sr-only" value={value} required={required} disabled={disabled} autoFocus={autoFocus}
      onFocus={() => root.current?.querySelector<HTMLButtonElement>("button")?.focus()}
      onInvalid={event => { event.preventDefault(); setInvalid(true); root.current?.querySelector<HTMLButtonElement>("button")?.focus(); }}
      onChange={event => { setInvalid(false); onChange(event.target.value); }}>
      <option value="" />{options.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
    </select>
    {invalid && <p id={`${id}-error`} role="alert" className="text-xs font-normal text-brand-red">Choose {label.toLowerCase()}.</p>}
  </div>;
}
